import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import {
  type StaffCandidate,
  StaffCandidatePicker,
  candidateName,
} from './StaffCandidatePicker.js';

/** 364 (#1217, design 1a): who the first staff member is. They are always the supplier's Host. */
type FirstStaffMode = 'existing' | 'new';

interface FormState {
  company_name: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  staff_email: string;
  staff_phone: string;
  /** 364: the picked existing user's id. */
  user_id: string;
}

const EMPTY: FormState = {
  company_name: '',
  email: '',
  phone: '',
  first_name: '',
  last_name: '',
  staff_email: '',
  staff_phone: '',
  user_id: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CreateSupplierResponse {
  supplier: { _id: string };
  user: { _id: string };
}

export function SupplierCreatePage() {
  const ability = useAbility();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [mode, setMode] = useState<FirstStaffMode>('existing');
  const [picked, setPicked] = useState<StaffCandidate | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<CreateSupplierResponse>('/suppliers', payload),
    onSuccess: () => {
      // Invalidate BEFORE navigating: the app-wide staleTime (30s) would
      // otherwise serve a stale suppliers list missing the new row.
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.supplier.list });
      navigate('/suppliers');
    },
    onError: (err: unknown) => {
      // Duplicate staff email is the only expected conflict — surface it on
      // the field so the admin corrects just the email (form data preserved).
      if (err instanceof ApiError && err.status === 409) {
        setErrors((prev) => ({
          ...prev,
          staff_email: 'This email already has an account. Choose "Existing user" to pick them.',
        }));
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create supplier');
      }
    },
  });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.company_name.trim() === '') next.company_name = 'Company name is required.';
    // 364: optional, but checked when given.
    if (form.email.trim() !== '' && !EMAIL_RE.test(form.email.trim()))
      next.email = 'Enter a valid email address.';
    if (mode === 'existing') {
      if (!picked) next.user_id = 'Pick the user from the search.';
    } else {
      if (form.first_name.trim() === '') next.first_name = 'First name is required.';
      if (!EMAIL_RE.test(form.staff_email.trim()))
        next.staff_email = 'Enter a valid email address.';
      if (form.staff_phone.trim() === '') next.staff_phone = 'Phone is required.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    mutate({
      company_name: form.company_name.trim(),
      ...(form.email.trim() !== '' ? { email: form.email.trim() } : {}),
      ...(form.phone.trim() !== '' ? { phone: form.phone.trim() } : {}),
      // 364: always the supplier's Host, so no role is sent.
      first_user:
        mode === 'existing' && picked
          ? { user_id: picked._id }
          : {
              first_name: form.first_name.trim(),
              ...(form.last_name.trim() !== '' ? { last_name: form.last_name.trim() } : {}),
              email: form.staff_email.trim(),
              phone: form.staff_phone.trim(),
            },
    });
  }

  // UI gating only — the backend enforces manage:Supplier on POST /suppliers.
  if (!ability.can('manage', 'Supplier')) return <Navigate to="/suppliers" replace />;

  return (
    <div>
      <PageHeader
        title="New Supplier"
        description="Onboard a supplier organization and invite its first staff member."
        fallback="/suppliers"
      />
      <form className="max-w-2xl space-y-6" onSubmit={onSubmit} noValidate>
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Business details</h2>
          <div className="space-y-4">
            <FormField
              label="Company Name"
              name="company_name"
              required
              error={errors.company_name}
            >
              <Input
                id="company_name"
                name="company_name"
                maxLength={200}
                value={form.company_name}
                onInput={(e) => update('company_name', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Company Email" name="email" optional error={errors.email}>
              <Input
                id="email"
                name="email"
                type="email"
                maxLength={320}
                value={form.email}
                onInput={(e) => update('email', (e.target as HTMLInputElement).value)}
              />
            </FormField>
            <FormField label="Phone" name="phone">
              <Input
                id="phone"
                name="phone"
                maxLength={50}
                value={form.phone}
                onInput={(e) => update('phone', (e.target as HTMLInputElement).value)}
              />
            </FormField>
          </div>
        </Card>
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">First staff member</h2>
          <div className="space-y-4">
            {/* 364 (design 1a): an existing user or a new one. */}
            <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-hairline-strong">
              {(
                [
                  ['existing', 'Existing user'],
                  ['new', 'New user'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={mode === value}
                  onClick={() => {
                    setMode(value);
                    setErrors({});
                  }}
                  className={`h-9 text-sm font-medium ${
                    mode === value ? 'bg-primary text-on-primary' : 'text-slate'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {mode === 'existing' ? (
              picked ? (
                <PickedUser candidate={picked} onChange={() => setPicked(null)} />
              ) : (
                <StaffCandidatePicker
                  error={errors.user_id}
                  onPick={(c) => {
                    setPicked(c);
                    setErrors((prev) => ({ ...prev, user_id: undefined }));
                  }}
                />
              )
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    label="First Name"
                    name="first_name"
                    required
                    error={errors.first_name}
                  >
                    <Input
                      id="first_name"
                      name="first_name"
                      maxLength={200}
                      value={form.first_name}
                      onInput={(e) => update('first_name', (e.target as HTMLInputElement).value)}
                    />
                  </FormField>
                  <FormField label="Last Name" name="last_name" optional>
                    <Input
                      id="last_name"
                      name="last_name"
                      maxLength={200}
                      value={form.last_name}
                      onInput={(e) => update('last_name', (e.target as HTMLInputElement).value)}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Staff Email"
                  name="staff_email"
                  required
                  error={errors.staff_email}
                  hint="Receives an invitation email with a password-setup link."
                >
                  <Input
                    id="staff_email"
                    name="staff_email"
                    type="email"
                    maxLength={320}
                    value={form.staff_email}
                    onInput={(e) => update('staff_email', (e.target as HTMLInputElement).value)}
                  />
                </FormField>
                <FormField label="Phone" name="staff_phone" required error={errors.staff_phone}>
                  <Input
                    id="staff_phone"
                    name="staff_phone"
                    type="tel"
                    maxLength={50}
                    placeholder="+60 12-345 6789"
                    value={form.staff_phone}
                    onInput={(e) => update('staff_phone', (e.target as HTMLInputElement).value)}
                  />
                </FormField>
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">Role</span>
              <div
                data-testid="first-staff-role"
                className="flex h-10 items-center justify-between rounded-lg border border-dashed border-hairline-strong bg-surface px-3 text-sm text-ink"
              >
                Host
                <span className="text-xs text-steel">The first staff member is always a Host</span>
              </div>
            </div>
          </div>
        </Card>
        {submitError && <p className="text-sm text-red-600">{submitError}</p>}
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Creating…' : 'Create Supplier'}
        </Button>
      </form>
    </div>
  );
}

/** 364 (designs 1c/1d): who was picked, what they are today, and what will change. */
function PickedUser({ candidate, onChange }: { candidate: StaffCandidate; onChange: () => void }) {
  const elsewhere = candidate.memberships;
  return (
    <div
      data-testid="picked-user"
      className="flex flex-col gap-1.5 rounded-xl border border-hairline bg-surface px-4 py-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-ink">{candidateName(candidate)}</span>
        <button type="button" className="text-sm text-primary underline" onClick={onChange}>
          Change
        </button>
      </div>
      <p className="text-sm text-slate">
        {[candidate.email, candidate.phone].filter(Boolean).join(' · ')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {candidate.is_customer && <Tag>Customer</Tag>}
        {elsewhere.map((m) => (
          <Tag key={m.supplier_id}>{`${m.role} at ${m.supplier_name}`}</Tag>
        ))}
      </div>
      {elsewhere.length > 0 ? (
        <p className="rounded-md bg-semantic-warning-bg px-2.5 py-2 text-sm text-semantic-warning-text">
          Also joins this supplier as Host. They switch between the two in the admin.
        </p>
      ) : (
        <p className="text-sm text-slate">
          {candidate.is_customer
            ? "Becomes this supplier's Host. Keeps booking as a customer."
            : "Becomes this supplier's Host."}
        </p>
      )}
    </div>
  );
}

function Tag({ children }: { children: string }) {
  return (
    <span className="inline-flex h-5 items-center rounded px-2 text-xs font-medium bg-brand-blue-soft text-primary">
      {children}
    </span>
  );
}
